import { useMemo } from 'react';
import { Grid, useTheme, alpha } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import {
    EditCalendar as ScheduleIcon,
    ContentCopy as CopyIcon,
    DeleteSweep as ClearIcon,
    ViewList as TemplateSetIcon,
    Assignment as TemplateIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import { themeConstants } from '@/theme';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    makeTooltipStyle,
    PIE_COLORS,
    renderPieLabel,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsScheduleSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();
    const s = data.schedule;

    const itemsPerDay = useMemo(
        () =>
            s.itemsPerDay.map(({ day, created, updated, deleted, copied }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                created,
                updated,
                deleted,
                copied: copied ?? 0,
            })),
        [s.itemsPerDay]
    );

    // CRUD bar data for templates (always global — not user-filtered)
    const templateCrudData = [
        { name: 'template created', count: s.templates.created },
        { name: 'template updated', count: s.templates.updated },
        { name: 'template deleted', count: s.templates.deleted },
        { name: 'set created', count: s.templates.setCreated },
        { name: 'set updated', count: s.templates.setUpdated },
        { name: 'set deleted', count: s.templates.setDeleted },
    ].filter((d) => d.count > 0);

    const totalActivity = s.itemsCreated + s.itemsUpdated + s.itemsDeleted;
    if (
        totalActivity === 0 &&
        s.copied === 0 &&
        s.templateSetApplied === 0 &&
        s.clearedAll === 0 &&
        templateCrudData.length === 0 &&
        s.topTemplateSets.length === 0 &&
        s.topTemplatesUsed.length === 0
    ) {
        return null;
    }

    // How many side chart columns are visible
    const sideChartMd = s.topTemplatesUsed.length > 0 && s.topTemplateSets.length > 0 ? 3 : 6;

    return (
        <MetricsSectionHeading
            icon={<ScheduleIcon fontSize="small" />}
            title={LL.metricsPage.sections.schedule()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* ── Stat cards ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.scheduleTemplatesTotal()}
                        value={s.templates.total ?? 0}
                        icon={<TemplateIcon />}
                        color="primary.main"
                        gradient={themeConstants.gradients.cardPrimary}
                        border={themeConstants.borders.primary}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.scheduleTemplateSetsTotal()}
                        value={s.templates.totalSets ?? 0}
                        icon={<TemplateSetIcon />}
                        color="secondary.main"
                        gradient={themeConstants.gradients.cardSecondary}
                        border={themeConstants.borders.secondary}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.scheduleCopied()}
                        value={s.copied}
                        icon={<CopyIcon />}
                        color="info.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.scheduleClearedAll()}
                        value={s.clearedAll}
                        icon={<ClearIcon />}
                        color="error.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.dark, 0.15)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.error.main, 0.3)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>

                {/* ── Activity per day — line chart ── */}
                {itemsPerDay.length > 0 && (
                    <Grid
                        size={{
                            xs: 12,
                            md:
                                s.topTemplatesUsed.length > 0 || s.topTemplateSets.length > 0
                                    ? 6
                                    : 12,
                        }}
                    >
                        <MetricsChartCard
                            title={LL.metricsPage.charts.scheduleItemsPerDay()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={itemsPerDay}
                                    margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="created"
                                        stroke={theme.palette.success.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.stats.scheduleItemsCreated()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="updated"
                                        stroke={theme.palette.info.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.stats.scheduleItemsUpdated()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="deleted"
                                        stroke={theme.palette.error.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.stats.scheduleItemsDeleted()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="copied"
                                        stroke={theme.palette.warning.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.stats.scheduleCopied()}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Top individual templates used — pie chart */}
                {s.topTemplatesUsed.length > 0 && (
                    <Grid size={{ xs: 12, md: sideChartMd }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.scheduleTopTemplatesUsed()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={s.topTemplatesUsed}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={90}
                                        dataKey="count"
                                        nameKey="name"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {s.topTemplatesUsed.map((_, i) => (
                                            <Cell
                                                key={i}
                                                fill={PIE_COLORS[i % PIE_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tt} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Top applied template sets — horizontal bar */}
                {s.topTemplateSets.length > 0 && (
                    <Grid size={{ xs: 12, md: sideChartMd }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.scheduleTopTemplateSets()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={s.topTemplateSets}
                                    layout="vertical"
                                    margin={{ left: 8, right: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tick={{ ...tickStyle, fontSize: 11 }}
                                        width={120}
                                    />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.primary.main}
                                        radius={[0, 4, 4, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Template & set CRUD — always global, no filter icon */}
                {templateCrudData.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.scheduleTemplateCrud()}
                            height={220}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={templateCrudData}
                                    layout="vertical"
                                    margin={{ left: 8, right: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tick={{ ...tickStyle, fontSize: 11 }}
                                        width={115}
                                    />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.warning.main}
                                        radius={[0, 4, 4, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
